import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Button, message, Modal, Descriptions, Select, Space, Typography, Input } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { apiClient } from 'Utils/api';

const { Title } = Typography;
const { TextArea } = Input;

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  NEW: { label: 'Neu', color: 'blue' },
  CONTACTED: { label: 'Kontaktiert', color: 'cyan' },
  NEGOTIATING: { label: 'Verhandlung', color: 'gold' },
  ACCEPTED: { label: 'Angenommen', color: 'green' },
  REJECTED: { label: 'Abgelehnt', color: 'red' },
  CLOSED: { label: 'Geschlossen', color: 'default' },
};

const CONDITION_LABELS: Record<string, string> = {
  NEW: 'Neu / Ungetragen',
  LIKE_NEW: 'Neuwertig',
  VERY_GOOD: 'Sehr gut',
  GOOD: 'Gut',
  WORN: 'Gebrauchsspuren',
  REPAIR_NEEDED: 'Reparaturbedürftig',
};

interface SellRequest {
  id: string;
  brand: string;
  model: string;
  referenceNumber: string | null;
  yearOfProduction: number | null;
  condition: string | null;
  hasBox: boolean;
  hasPapers: boolean;
  desiredPriceCents: number | null;
  imageUrls: string[] | null;
  customerName: string;
  customerEmail: string;
  customerPhone: string | null;
  customerMessage: string | null;
  status: string;
  internalNotes: string | null;
  createdAt: string;
}

const SellRequestsPage: React.FC = () => {
  const [items, setItems] = useState<SellRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<SellRequest | null>(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/admin/sell-requests');
      setItems(res.data.sellRequests || []);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await apiClient.patch(`/api/admin/sell-requests/${id}`, { status });
      message.success('Status aktualisiert');
      fetchAll();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      await apiClient.patch(`/api/admin/sell-requests/${selected.id}`, { internalNotes: notesDraft });
      setSelected({ ...selected, internalNotes: notesDraft });
      message.success('Notizen gespeichert');
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    } finally {
      setSavingNotes(false);
    }
  };

  const formatPrice = (cents: number | null) =>
    cents ? `${(cents / 100).toLocaleString('de-DE')} €` : '–';

  const apiBase = process.env.REACT_APP_API_URL || '';

  const columns = [
    {
      title: 'Datum',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => new Date(v).toLocaleString('de-DE'),
    },
    { title: 'Marke', dataIndex: 'brand', key: 'brand' },
    { title: 'Modell', dataIndex: 'model', key: 'model' },
    {
      title: 'Kunde',
      key: 'customer',
      render: (_: any, r: SellRequest) => (
        <div>
          <div>{r.customerName}</div>
          <small style={{ color: '#888' }}>{r.customerEmail}</small>
        </div>
      ),
    },
    {
      title: 'Wunsch',
      dataIndex: 'desiredPriceCents',
      key: 'desiredPriceCents',
      render: formatPrice,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status: string, r: SellRequest) => (
        <Select
          value={status}
          style={{ width: 160 }}
          onChange={(v) => updateStatus(r.id, v)}
          options={Object.entries(STATUS_LABELS).map(([k, v]) => ({
            value: k,
            label: <Tag color={v.color}>{v.label}</Tag>,
          }))}
        />
      ),
    },
    {
      title: 'Aktion',
      key: 'action',
      width: 100,
      render: (_: any, r: SellRequest) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => {
            setSelected(r);
            setNotesDraft(r.internalNotes || '');
          }}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>Verkaufsanfragen</Title>
      <Card>
        <Table
          rowKey="id"
          dataSource={items}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title={selected ? `${selected.brand} ${selected.model} – ${selected.customerName}` : ''}
        open={!!selected}
        onCancel={() => setSelected(null)}
        footer={null}
        width={760}
      >
        {selected && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Marke">{selected.brand}</Descriptions.Item>
              <Descriptions.Item label="Modell">{selected.model}</Descriptions.Item>
              <Descriptions.Item label="Referenz">{selected.referenceNumber || '–'}</Descriptions.Item>
              <Descriptions.Item label="Baujahr">{selected.yearOfProduction || '–'}</Descriptions.Item>
              <Descriptions.Item label="Zustand">
                {selected.condition ? CONDITION_LABELS[selected.condition] : '–'}
              </Descriptions.Item>
              <Descriptions.Item label="Wunschpreis">{formatPrice(selected.desiredPriceCents)}</Descriptions.Item>
              <Descriptions.Item label="Box">{selected.hasBox ? 'Ja' : 'Nein'}</Descriptions.Item>
              <Descriptions.Item label="Papiere">{selected.hasPapers ? 'Ja' : 'Nein'}</Descriptions.Item>
              <Descriptions.Item label="Name">{selected.customerName}</Descriptions.Item>
              <Descriptions.Item label="E-Mail">
                <a href={`mailto:${selected.customerEmail}`}>{selected.customerEmail}</a>
              </Descriptions.Item>
              <Descriptions.Item label="Telefon" span={2}>
                {selected.customerPhone ? (
                  <a href={`tel:${selected.customerPhone}`}>{selected.customerPhone}</a>
                ) : '–'}
              </Descriptions.Item>
              <Descriptions.Item label="Nachricht" span={2}>
                {selected.customerMessage || '–'}
              </Descriptions.Item>
            </Descriptions>

            {selected.imageUrls && selected.imageUrls.length > 0 && (
              <div>
                <Title level={5}>Bilder</Title>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                  {selected.imageUrls.map((url) => (
                    <a key={url} href={`${apiBase}${url}`} target="_blank" rel="noreferrer">
                      <img
                        src={`${apiBase}${url}`}
                        alt=""
                        style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 4 }}
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Title level={5}>Interne Notizen</Title>
              <TextArea
                rows={4}
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
              />
              <div style={{ textAlign: 'right', marginTop: 8 }}>
                <Button type="primary" loading={savingNotes} onClick={saveNotes}>
                  Notizen speichern
                </Button>
              </div>
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default SellRequestsPage;
